import { useEffect, useMemo, useRef, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeader from '@/components/feature/PageHeader';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import WorkCard from '@/components/feature/WorkCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { workItems } from '@/mocks/works';

const PAGE_SIZE = 6;

const typeOptions = ['全部', '动画', '漫画'];
const regionOptions = ['全部', '日本', '中国', '欧美'];
const yearOptions = ['全部', ...Array.from(new Set(workItems.map((item) => item.year)))
  .sort((a, b) => b - a)
  .map((year) => String(year))];

const sortOptions = [
  { label: '最近更新', value: 'updated' },
  { label: '知名度优先', value: 'popularity' },
];

function FilterGroup({
  title,
  options,
  value,
  onChange,
}: {
  title: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="py-4 border-b border-background-200/70 last:border-0 last:pb-0 first:pt-0">
      <p className="text-[11px] tracking-[0.16em] uppercase text-secondary-500 font-label">{title}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`h-8 px-3 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
              value === option
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:text-primary-700'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function WorksCategoryPage() {
  usePageMeta({
    title: '动漫作品 - 琉璃神社',
    description:
      '琉璃神社动漫作品档案，收录动画与漫画作品的类型、地区、年份、制作公司与简介，支持多维筛选与知名度排序。',
    keywords: '动漫作品,动画推荐,漫画作品,作品档案,动画制作,琉璃神社',
  });

  const [type, setType] = useState('全部');
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState('updated');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    return workItems
      .filter((item) => (type === '全部' ? true : item.type === type))
      .filter((item) => (region === '全部' ? true : item.region === region))
      .filter((item) => (year === '全部' ? true : String(item.year) === year))
      .sort((a, b) => {
        if (sort === 'popularity') return b.popularity - a.popularity;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
  }, [type, region, year, sort]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type, region, year, sort]);

  const shown = filtered.slice(0, visible);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, Math.ceil(visible / PAGE_SIZE)), totalPages);

  const resetAll = () => {
    setType('全部');
    setRegion('全部');
    setYear('全部');
    setSort('updated');
  };

  const changePage = (page: number) => {
    setVisible(page * PAGE_SIZE);
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <SiteLayout>
      <PageHeader
        eyebrow="Works"
        title="动漫作品"
        description="动画与漫画作品的推荐与介绍。按类型、地区与年份筛选，查看剧情简述、制作资料与相关作品。"
        breadcrumb={[{ label: '动漫作品' }]}
      />

      <div className="shell py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
          <aside className="w-full lg:w-[248px] shrink-0">
            <button
              type="button"
              onClick={() => setFilterOpen((prev) => !prev)}
              className="lg:hidden w-full inline-flex items-center justify-between h-11 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 whitespace-nowrap cursor-pointer"
            >
              <span className="inline-flex items-center gap-2">
                <i className="ri-filter-3-line w-4 h-4 flex items-center justify-center"></i>
                筛选作品
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="text-[11px] text-secondary-500 font-label">
                  {type === '全部' && region === '全部' && year === '全部' ? '未筛选' : '已筛选'}
                </span>
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${filterOpen ? 'rotate-180' : ''}`}></i>
              </span>
            </button>

            <div className={`${filterOpen ? 'block' : 'hidden'} lg:block mt-3 lg:mt-0`}>
              <div className="lg:sticky lg:top-24 rounded-lg border border-background-200/80 bg-background-50 p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-foreground-900">筛选条件</p>
                  <button
                    type="button"
                    onClick={resetAll}
                    className="text-[11px] text-secondary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    重置
                  </button>
                </div>
                <FilterGroup title="作品类型" options={typeOptions} value={type} onChange={setType} />
                <FilterGroup title="地区" options={regionOptions} value={region} onChange={setRegion} />
                <FilterGroup title="年份" options={yearOptions} value={year} onChange={setYear} />
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-background-200/70">
              <p className="text-xs text-secondary-500">
                共 <span className="text-foreground-900 font-label">{filtered.length}</span> 部作品
                {filtered.length > 0 && (
                  <span className="ml-2 text-secondary-400">
                    · 已显示 {shown.length} 部
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-[11px] tracking-[0.14em] uppercase text-secondary-500 font-label whitespace-nowrap">
                  排序
                </span>
                <div className="relative flex items-center">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    aria-label="作品排序方式"
                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-background-200 bg-background-50 text-xs text-foreground-800 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-2.5 w-4 h-4 flex items-center justify-center text-secondary-400 pointer-events-none"></i>
                </div>
              </div>
            </div>

            <div ref={listRef} className="mt-6 scroll-mt-24">
              {shown.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                    {shown.map((item) => (
                      <WorkCard key={item.id} item={item} />
                    ))}
                  </div>

                  {visible < filtered.length && (
                    <div className="mt-8 flex flex-col items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                        className="inline-flex items-center gap-2 h-11 px-7 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        加载更多作品
                        <i className="ri-arrow-down-line w-4 h-4 flex items-center justify-center"></i>
                      </button>
                      <span className="text-[11px] text-secondary-400 font-label">
                        已显示 {shown.length} / {filtered.length}
                      </span>
                    </div>
                  )}

                  <Pagination
                    total={filtered.length}
                    pageSize={PAGE_SIZE}
                    page={currentPage}
                    onChange={changePage}
                  />
                </>
              ) : (
                <EmptyState
                  icon="ri-book-open-line"
                  title="没有找到符合条件的作品"
                  description="换一个类型、地区或年份试试，也可以直接重置筛选查看全部作品档案。"
                  actionLabel="重置筛选条件"
                  onAction={resetAll}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}